import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { isSupported, currentPermission, subscribeWebPush } from '../lib/webPush';

const DISMISSED_KEY = 'cricket_webpush_dismissed_until';

/**
 * Soft prompt for browser push.
 *
 * Rules:
 *   - Only rendered when push is supported AND permission is 'default'.
 *   - Hidden after dismiss for 7 days (localStorage timestamp).
 *   - Calling subscribeWebPush() must be from a user gesture, so the
 *     button is the actual permission trigger.
 *
 * Mounted on the AthleteDashboard. Self-hides when permission flips to
 * granted or denied.
 */
export default function WebPushPrompt() {
  const { token } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) return;
      if (!(await isSupported())) return;
      const perm = await currentPermission();
      if (perm !== 'default') return;
      try {
        const until = parseInt(localStorage.getItem(DISMISSED_KEY) || '0', 10);
        if (until && Date.now() < until) return;
      } catch (_) { /* ignore */ }
      setShow(true);
    })();
  }, [token]);

  const enable = async () => {
    setBusy(true);
    const res = await subscribeWebPush(token);
    setBusy(false);
    if (res.ok) {
      toast.success('Notifications on — daily clip drops at 5pm.');
      setShow(false);
    } else if (res.reason === 'webpush_not_configured') {
      // VAPID keys aren't set on the backend; silently retire.
      setShow(false);
    } else if (res.reason === 'permission_denied') {
      toast.info('No worries — enable later from your browser settings.');
      setShow(false);
    } else {
      toast.error('Could not enable notifications.');
    }
  };

  const dismiss = () => {
    try {
      localStorage.setItem(
        DISMISSED_KEY,
        String(Date.now() + 7 * 24 * 60 * 60 * 1000),
      );
    } catch (_) { /* ignore */ }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="rounded-2xl p-4 flex flex-wrap items-center gap-3"
      style={{
        background: 'linear-gradient(135deg, rgba(0,166,81,0.10), rgba(0,217,255,0.08))',
        border: '1px solid rgba(0,201,167,0.25)',
      }}
    >
      <div className="text-2xl">🔔</div>
      <div className="flex-1 min-w-0">
        <div className="font-bold text-sm">Get the daily clip drop</div>
        <div className="text-xs opacity-70">
          One push at 5pm with a pro clip picked for your athlete. No spam.
        </div>
      </div>
      <button
        onClick={enable}
        disabled={busy}
        className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white"
        style={{ background: 'linear-gradient(135deg, #00A651, #00D9FF)' }}
      >
        {busy ? 'Enabling…' : 'Enable'}
      </button>
      <button
        onClick={dismiss}
        className="text-xs opacity-60 hover:opacity-100"
      >
        Not now
      </button>
    </div>
  );
}
